import { ERAS } from '../game/constants';
import { formatNumber, formatRate } from '../game/utils';

interface ResourceBarProps {
  energy: number;
  totalEnergyEarned: number;
  energyPerSecond: number;
  currentEraIndex: number;
}

export function ResourceBar({
  energy,
  totalEnergyEarned,
  energyPerSecond,
  currentEraIndex,
}: ResourceBarProps) {
  const currentEra = ERAS[currentEraIndex];
  const nextEra = ERAS[currentEraIndex + 1];

  if (!currentEra) return null;

  const progressPct = nextEra
    ? Math.min(
        100,
        ((totalEnergyEarned - currentEra.requiredEnergy) /
          (nextEra.requiredEnergy - currentEra.requiredEnergy)) *
          100,
      )
    : 100;

  return (
    <div className="sticky top-[49px] z-40 border-b border-gray-700/80 bg-gray-900/95 px-4 py-3 backdrop-blur-sm">
      <div className="mx-auto flex max-w-5xl flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold tabular-nums text-white">
              ⚡ {formatNumber(energy)}
            </span>
            {energyPerSecond > 0 && (
              <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-xs font-medium text-blue-300">
                +{formatRate(energyPerSecond)}/s
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-gray-800/80 px-3 py-1">
            <span>{currentEra.icon}</span>
            <span className="text-sm font-semibold text-gray-200">{currentEra.name}</span>
          </div>
        </div>

        {nextEra ? (
          <div className="flex items-center gap-3">
            <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="shrink-0 text-xs text-gray-500">
              {nextEra.icon} {formatNumber(nextEra.requiredEnergy)}
            </span>
          </div>
        ) : (
          <p className="text-center text-xs font-medium text-violet-400">✨ Maximum evolution reached</p>
        )}
      </div>
    </div>
  );
}
